import type {Moment} from 'moment-timezone'
import moment from 'moment-timezone'
import type {ParserT} from '@freckle/parser'
import {
  Parser,
  string,
  record,
  array,
  number,
  boolean,
  date,
  stringEnum,
  nullable,
  nullableDefined,
  nullableDefault,
  mapStatic
} from '@freckle/parser'
import type {Environment} from '@freckle/educator-entities/ts/common/helpers/environment'
import {Environments} from '@freckle/educator-entities/ts/common/helpers/environment'
import type {LangT} from '@freckle/educator-entities/ts/common/helpers/languages'
import {Languages} from '@freckle/educator-entities/ts/common/helpers/languages'
import type {Leaderboard} from '@freckle/educator-entities/ts/common/helpers/leaderboard'
import {Leaderboards} from '@freckle/educator-entities/ts/common/helpers/leaderboard'
import StorageHelper from '@freckle/educator-entities/ts/common/helpers/storage-helper'
import type {SubjectT} from '@freckle/educator-entities/ts/common/helpers/subjects'
import {Subjects} from '@freckle/educator-entities/ts/common/helpers/subjects'
import type {TeacherRole} from '@freckle/educator-entities/ts/common/helpers/teacher-role'
import {TeacherRoles} from '@freckle/educator-entities/ts/common/helpers/teacher-role'
import type {ElaLevelingProgramT} from '@freckle/educator-entities/ts/ela/common/logic/ela-leveling-program'
import {ElaLevelingPrograms} from '@freckle/educator-entities/ts/ela/common/logic/ela-leveling-program'
import {fancyClient, extractData} from '@freckle/educator-entities/ts/openapi-clients'
import type {IdentityProviderT} from '@freckle/educator-entities/ts/roster/identity-provider'
import {parser as identityProviderParser} from '@freckle/educator-entities/ts/roster/identity-provider'

export type StudentNameSortFieldT = 'first_name' | 'last_name'

export const StudentNameSortField = {
  FirstName: 'first_name' as StudentNameSortFieldT,
  LastName: 'last_name' as StudentNameSortFieldT,

  all(): Array<StudentNameSortFieldT> {
    return [StudentNameSortField.FirstName, StudentNameSortField.LastName]
  },
  parse(s: string): StudentNameSortFieldT | undefined | null {
    return StudentNameSortField.all().find(sortField => sortField === s)
  },
  forDisplay(s: StudentNameSortFieldT): string {
    return s === StudentNameSortField.FirstName ? 'First Name' : 'Last Name'
  }
}

//Only add properties than can be shared across all apps.
export type BaseTeacherAttrs = {
  id: number
  email: string
  emailVerified: boolean
  firstName: string
  lastName: string
  language: LangT
  countryCode: string | undefined | null
  createdAt: Moment
  schoolId: number | undefined | null
  leaderboard: Leaderboard
  focusMode: boolean
  canChangeEmail: boolean
  environment: Environment | undefined | null
  role: TeacherRole | undefined | null
  cleverTeacherId: string | undefined | null
  classLinkTeacherId: string | undefined | null
  renaissanceRPIdentifier: string | undefined | null
  googleSignin: boolean
  gurId: string | null
  mathStandardSetId: string // Teacher preference standard set id
  elaStandardSetId: string | undefined | null
  onboardingGrades: Array<number> | undefined | null
  courseSubjects: Array<SubjectT> | undefined | null
  studentNameSortField: StudentNameSortFieldT
  elaLevelingProgram: ElaLevelingProgramT | undefined | null
  identityProvider: IdentityProviderT | undefined | null
  idpManaged: boolean
  classroomLicensePurchasedAt: Moment | null
}

export const parseTeacherAttrs: ParserT<BaseTeacherAttrs> = Parser.map<
  BaseTeacherAttrs,
  BaseTeacherAttrs
>(
  record({
    id: number(),
    email: string(),
    emailVerified: boolean(),
    firstName: string(),
    lastName: string(),
    language: Languages.parseT(),
    countryCode: nullable(string()),
    createdAt: mapStatic(date(), moment),
    schoolId: nullable(number()),
    leaderboard: stringEnum('Leaderboard', Leaderboards.parse),
    focusMode: boolean(),
    canChangeEmail: boolean(),
    googleSignin: nullableDefault(boolean(), false),
    environment: nullable(stringEnum('Environment', Environments.parse)),
    role: nullable(stringEnum('TeacherRole', TeacherRoles.parse)),
    cleverTeacherId: nullable(string()),
    classLinkTeacherId: nullable(string()),
    gurId: nullableDefined(string()),
    renaissanceRPIdentifier: nullable(string()),
    mathStandardSetId: string(),
    elaStandardSetId: nullable(string()),
    onboardingGrades: nullable(array(number())),
    courseSubjects: nullable(array(stringEnum('SubjectT', Subjects.parse))),
    studentNameSortField: stringEnum('StudentNameSortFieldT', StudentNameSortField.parse),
    elaLevelingProgram: nullable(stringEnum('ElaLevelingProgramT', ElaLevelingPrograms.parse)),
    identityProvider: nullable(identityProviderParser),
    idpManaged: boolean(),
    classroomLicensePurchasedAt: nullableDefined(mapStatic(date(), moment))
  }),
  'checkDemoMode',
  parsedResponse => {
    // Hide Teacher's Name and email when in demoMode
    const demoMode = StorageHelper.getLocalStoragePropertyDefault('demoMode', false)
    const updatedResponse = updateBaseResponseProperties(parsedResponse, demoMode)
    return updatedResponse
  }
)

export const parse = Parser.mkRun<BaseTeacherAttrs>(parseTeacherAttrs)

// Because Teacher has different metadata depending on the app
// The following functions expect something in the Shape of the common attributes

export function updateBaseResponseProperties<
  ExtraAttrs,
  AllAttrs extends Record<string, unknown> & BaseTeacherAttrs & ExtraAttrs
>(teacherAttrs: AllAttrs, isDemoMode: boolean): AllAttrs {
  const updatedResponse = {
    email: isDemoMode ? 'demo@freckle.com' : teacherAttrs.email,
    firstName: isDemoMode ? 'Demo' : teacherAttrs.firstName,
    lastName: isDemoMode ? 'Account' : teacherAttrs.lastName
  }
  return {...teacherAttrs, ...updatedResponse}
}

export function formattedName(teacher: Inexact<{firstName: string; lastName: string}>): string {
  return `${teacher.firstName} ${teacher.lastName}`
}

export async function fetchTeachersAsASchoolAdmin(): Promise<Array<BaseTeacherAttrs>> {
  return fancyClient
    .GET('/2/school-admins/me/teachers')
    .then(extractData)
    .then(teachers =>
      teachers.map(t => ({
        ...t,
        language: t.language as LangT,
        countryCode: t.countryCode,
        createdAt: moment(t.createdAt),
        schoolId: t.schoolId,
        environment: t.environment,
        role: t.role,
        cleverTeacherId: t.cleverTeacherId,
        classLinkTeacherId: t.classLinkTeacherId,
        renaissanceRPIdentifier: t.renaissanceRPIdentifier,
        gurId: t.gurId ?? null,
        elaStandardSetId: t.elaStandardSetId,
        onboardingGrades: null,
        courseSubjects: null,
        googleSignin: false,
        elaLevelingProgram: t.elaLevelingProgram,
        identityProvider: t.identityProvider as IdentityProviderT | undefined | null,
        classroomLicensePurchasedAt:
          t.classroomLicensePurchasedAt !== null &&
          t.classroomLicensePurchasedAt !== undefined &&
          t.classroomLicensePurchasedAt !== ''
            ? moment(t.classroomLicensePurchasedAt)
            : null
      }))
    )
}
